import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, Power, PowerOff, Check, MousePointerClick,
  CheckCircle2, Clock, Megaphone,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import adminApi from '../lib/adminApi';

const TIPOS = [
  { value: 'slider', label: 'Slider (R$79/7d)' },
  { value: 'card', label: 'Card (R$39/7d)' },
];

function AnuncioForm({ inicial, anunciantes, onSalvar, onCancelar, salvando }) {
  const [form, setForm] = useState(inicial || {
    titulo: '', descricao: '', tipo: 'card', imagemUrl: '', logoUrl: '', urlDestino: '#',
    anuncianteId: 'manual', ativo: true, aprovado: true,
  });

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSalvar(form); }}
      className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3 mb-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Título</label>
          <input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required data-testid="anuncio-titulo-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo</label>
          <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} data-testid="anuncio-tipo-select" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]">
            {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Descrição</label>
        <input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">URL da imagem</label>
          <input value={form.imagemUrl} onChange={(e) => setForm({ ...form, imagemUrl: e.target.value })} required placeholder="https://..." data-testid="anuncio-imagem-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">URL de destino</label>
          <input value={form.urlDestino} onChange={(e) => setForm({ ...form, urlDestino: e.target.value })} placeholder="#" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Anunciante</label>
        <select value={form.anuncianteId || 'manual'} onChange={(e) => setForm({ ...form, anuncianteId: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]">
          <option value="manual">— Manual / Avulso —</option>
          {anunciantes.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} className="w-4 h-4 accent-[#1D9E75]" />
          <span>Ativo</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.aprovado} onChange={(e) => setForm({ ...form, aprovado: e.target.checked })} className="w-4 h-4 accent-[#1D9E75]" />
          <span>Aprovado</span>
        </label>
      </div>

      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={salvando} data-testid="salvar-anuncio-btn" className="flex-1 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-2 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
          {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
        </button>
        <button type="button" onClick={onCancelar} className="px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl py-2 text-sm">Cancelar</button>
      </div>
    </form>
  );
}

export default function AdminAnuncios() {
  const [lista, setLista] = useState([]);
  const [anunciantes, setAnunciantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [a, an] = await Promise.all([
        adminApi.get('/admin/anuncios'),
        adminApi.get('/admin/anunciantes'),
      ]);
      setLista(a.data);
      setAnunciantes(an.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleCriar = async (form) => {
    setSalvando(true);
    try {
      await adminApi.post('/admin/anuncios', form);
      setCriando(false); carregar();
    } catch (err) { alert(err?.response?.data?.detail || 'Erro'); }
    finally { setSalvando(false); }
  };

  const handleAtualizar = async (form) => {
    setSalvando(true);
    try {
      await adminApi.patch(`/admin/anuncios/${editando.id}`, form);
      setEditando(null); carregar();
    } catch (err) { alert(err?.response?.data?.detail || 'Erro'); }
    finally { setSalvando(false); }
  };

  const toggle = async (a, campo) => {
    await adminApi.patch(`/admin/anuncios/${a.id}`, { [campo]: !a[campo] });
    carregar();
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir anúncio "${a.titulo}"?`)) return;
    await adminApi.delete(`/admin/anuncios/${a.id}`);
    carregar();
  };

  const nomeAnunciante = (id) => anunciantes.find(x => x.id === id)?.nome || '—';

  return (
    <AdminLayout title="Anúncios">
      {!criando && !editando && (
        <button onClick={() => setCriando(true)} data-testid="novo-anuncio-btn" className="mb-4 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-4 py-2.5 text-sm flex items-center gap-2">
          <Plus size={15} /> Novo anúncio
        </button>
      )}

      {criando && <AnuncioForm anunciantes={anunciantes} onSalvar={handleCriar} onCancelar={() => setCriando(false)} salvando={salvando} />}
      {editando && <AnuncioForm inicial={editando} anunciantes={anunciantes} onSalvar={handleAtualizar} onCancelar={() => setEditando(null)} salvando={salvando} />}

      {loading ? (
        <div className="py-12 text-center text-gray-400 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </div>
      ) : lista.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
          <Megaphone size={28} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nenhum anúncio cadastrado.</p>
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
          {lista.map((a) => (
            <div key={a.id} data-testid={`anuncio-${a.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex gap-3">
              <div className="w-20 h-20 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                {a.imagemUrl && <img src={a.imagemUrl} alt={a.titulo} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm font-semibold truncate ${a.ativo && a.aprovado ? 'text-gray-900' : 'text-gray-400'}`}>{a.titulo}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${a.tipo === 'slider' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {a.tipo}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5">{a.descricao || '—'}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1"><MousePointerClick size={11} /> {a.totalCliques || 0} cliques</span>
                  <span>· {nomeAnunciante(a.anuncianteId)}</span>
                </div>

                <div className="flex items-center gap-1 mt-3">
                  <button onClick={() => toggle(a, 'aprovado')} title={a.aprovado ? 'Reprovar' : 'Aprovar'} data-testid={`aprovar-${a.id}`} className={`p-1.5 rounded-lg ${a.aprovado ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-amber-500 hover:bg-amber-50'}`}>
                    {a.aprovado ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                  </button>
                  <button onClick={() => toggle(a, 'ativo')} title={a.ativo ? 'Desativar' : 'Ativar'} data-testid={`toggle-anuncio-${a.id}`} className={`p-1.5 rounded-lg ${a.ativo ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-gray-400 hover:bg-gray-100'}`}>
                    {a.ativo ? <Power size={14} /> : <PowerOff size={14} />}
                  </button>
                  <button onClick={() => setEditando(a)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Pencil size={14} /></button>
                  <button onClick={() => excluir(a)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
